<template>
  <AppDialog
    key="insights-collaboration-integrations"
    append-to-body
    :show="show"
    size="medium"
    position="centered"
    title="Communication integrations"
    class="insights-collaboration-integrations-dialog"
    @close="closeDialog"
  >
    <p>
      Connect the communication integrations your organization uses to see emails, calls, and messages in this
      widget.
    </p>
    <p>
      <strong>Setup happens once per integration.</strong> After that, every member's data syncs automatically. All
      dates and times are shown in UTC.
    </p>

    <div class="providers-table-wrapper">
      <table class="providers-table">
        <thead>
          <tr>
            <th>Integration</th>
            <th>Data tracked</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="provider in rows"
            :key="provider.type"
            :class="provider.type"
          >
            <td>
              <div class="provider">
                <img
                  class="provider-logo"
                  :src="provider.logo.src"
                  :alt="provider.logo.alt"
                />
                <strong>{{ provider.name }}</strong>
              </div>
            </td>
            <td>{{ provider.dataTracked }}</td>
            <td>
              <span :class="['status-badge', provider.status]">{{ statusLabels[provider.status] }}</span>
              <a
                v-if="provider.status === 'connected'"
                class="members-count"
                :href="provider.members_url"
              >
                <i class="material-symbols-rounded">group</i>
                {{ provider.connected_members_count }}/{{ membersCount }} connected
              </a>
            </td>
            <td>
              <form
                v-if="provider.status === 'not_connected'"
                :id="formId(provider.type)"
                method="post"
                :action="provider.connect_url"
              >
                <input
                  name="authenticity_token"
                  type="hidden"
                  :value="token"
                />
                <AppButton
                  type="primary"
                  class="connect-btn"
                  @click="connect(provider)"
                >
                  Connect
                </AppButton>
              </form>
              <a
                v-else
                class="btn btn-default manage-btn"
                :href="integrationsUrl"
              >
                Manage
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </AppDialog>
</template>

<script>
import { INSIGHTS, INSIGHTS_ASSETS } from '~/app_constants';
import { InsightsTracking } from '~/mixins';
import AppButton from '~/components/AppButton.vue';
import AppDialog from '~/components/AppDialog.vue';

export default {
  name: 'InsightsCollaborationIntegrationsDialog',
  components: {
    AppDialog,
    AppButton,
  },
  mixins: [InsightsTracking],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // The widget's providers, in display order
    providers: {
      type: Array,
      required: true,
    },
    membersCount: {
      type: Number,
      default: null,
    },
    integrationsUrl: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  data: () => ({
    statusLabels: Object.freeze({
      not_connected: 'Not connected',
      connected: 'Connected',
      disabled: 'Disabled',
    }),
  }),
  computed: {
    token() {
      return document.querySelector('meta[name="csrf-token"]')?.content;
    },
    rows() {
      return this.providers.map((provider) => ({
        ...provider,
        ...INSIGHTS.collaborations.providers[provider.type],
        logo: INSIGHTS_ASSETS.collaboration_provider_logos[provider.type],
      }));
    },
  },
  methods: {
    formId(type) {
      return `collaborations_connect_${type}`;
    },
    connect(provider) {
      this.trackAnalytics('Widget click through', {
        widget_name: 'collaborations',
        link_type: `${provider.linkType} setup button`,
      });

      // Small delay to allow the event to be tracked
      setTimeout(() => {
        document.getElementById(this.formId(provider.type)).submit();
      }, 250);
    },
    closeDialog() {
      this.$emit('close');
    },
  },
};
</script>
